import React, {
  createContext,
  useContext,
  useMemo,
  ReactNode,
  useState,
  useCallback,
  useEffect,
} from 'react'
import { searchAllInbox, searchAllBellNotification } from '@/api/index'
import useSWR from 'swr'
import useAxios from '@/utilities/useAxios'

const DND_SERVICE = `${import.meta.env.VITE_DND_APP_BACKEND_URL}`

type InboxManagementContextType = {
  inboxes: any[] | undefined
  bellNotifications: any[] | undefined
  mutateInboxes: () => Promise<any>
  mutateBellNotifications: () => Promise<any>
  page: number
  setPage: React.Dispatch<React.SetStateAction<number>>
  rowsPerPage: number
  setRowsPerPage: React.Dispatch<React.SetStateAction<number>>
  isLoading: boolean
  optimizedSearchInboxes: (keyword: string) => void
  inboxId: string
  totalElem: number
}

const InboxManagementContext = createContext<
  InboxManagementContextType | undefined
>(undefined)

export const useInboxManagementContext = (): InboxManagementContextType => {
  const context = useContext(InboxManagementContext)
  if (!context) {
    throw new Error(
      'useInboxManagementContext must be used within a InboxManagementContextProvider',
    )
  }
  return context
}

type InboxManagementContextProviderProps = {
  children: ReactNode
}

export const InboxManagementContextProvider: React.FC<
  InboxManagementContextProviderProps
> = ({ children }) => {
  const inboxId = localStorage.getItem('inboxId') || '0'

  const [page, setPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [keyword, setKeyword] = useState('')

  const [data, setData] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  const [totalElem, setTotalElem] = useState(0)

  const getAllInboxes = async () => {
    setIsLoading(true)
    try {
      const response = await searchAllInbox({
        keyword,
        startCreatedDate: '',
        endCreatedDate: '',
        startModifiedDate: '',
        endModifiedDate: '',
        page,
        size: rowsPerPage,
        sort: 'dateCreated,desc',
        pageable: {},
      })

      const inboxList = response.data.data.data
      const total = response.data.data.totalElements
      setTotalElem(total)
      return inboxList
    } catch (error) {
      console.error('Failed to fetch inboxes:', error)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const getBellNotifications = async () => {
    try {
      const response = await searchAllBellNotification({
        keyword: '',
        startCreatedDate: '',
        endCreatedDate: '',
        startModifiedDate: '',
        endModifiedDate: '',
        page: 0,
        size: 5,
        sort: 'dateCreated,desc',
        isRead: 'false',
        pageable: {},
      })
      return response.data.data
    } catch (error) {
      console.error('Failed to fetch bell notifications:', error)
      throw error
    }
  }

  const debounce = <T extends (...args: any[]) => any>(
    func: T,
  ): ((...args: Parameters<T>) => void) => {
    let timer: NodeJS.Timeout | null = null

    return function (this: ThisParameterType<T>, ...args: Parameters<T>) {
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => {
        timer = null
        func.apply(this, args)
      }, 500)
    }
  }

  const handleChange = (keyword: string) => {
    setKeyword(keyword)
    setPage(1)
    useAxios
      .post(`${DND_SERVICE}/v1/notification/user/search`, {
        params: {
          keyword,
          startCreatedDate: '',
          endCreatedDate: '',
          startModifiedDate: '',
          endModifiedDate: '',
          page,
          size: rowsPerPage,
          sort: 'dateCreated,desc',
          pageable: {},
        },
      })
      .then((res) => res.data)
      .then((json) => setData(json?.data ?? []))
  }

  const optimizedSearchInboxes = useCallback(debounce(handleChange), [])

  const { data: inboxes, mutate: mutateInboxes } = useSWR(
    `/v1/notification/user/search?keyword=${keyword}&page=${page}&size=${rowsPerPage}`,
    getAllInboxes,
    { revalidateOnFocus: true },
  )

  const { data: bellNotifications, mutate: mutateBellNotifications } = useSWR(
    `/v1/notification/user/search`,
    getBellNotifications,
    { revalidateOnFocus: false },
  )

  useEffect(() => {
    mutateInboxes()
  }, [totalElem])

  const value = useMemo(
    () => ({
      inboxes,
      mutateInboxes,
      page,
      setPage,
      rowsPerPage,
      setRowsPerPage,
      isLoading,
      optimizedSearchInboxes,
      inboxId,
      totalElem,
      bellNotifications,
      mutateBellNotifications,
    }),
    [inboxes, page, rowsPerPage, isLoading, data, totalElem],
  )

  return (
    <InboxManagementContext.Provider value={value}>
      {children}
    </InboxManagementContext.Provider>
  )
}
